import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Card } from '../../components/Card/Card';
import './Commissioning.scss';
import { Button } from 'src/components/Button/Button';
import { useNavigate } from 'react-router-dom';
import { AngleLeft } from 'src/icons/AngleLeft';
import { List } from './RoomList/List';
import { fetchAuthenticated } from 'src/controllers';
import { BannerMessageContext } from 'src/shared/contexts/BannerMessageContext';
import { URL_VERSION, WAS_BACK_END_API_URL } from 'src/constants';
import { v4 } from 'uuid';
import { largeSpinner, LoadingSpinner, smallSpinner } from 'src/components/common/loader/LoadingSpinner';
import { XClose } from 'src/icons/XClose';
import { Send } from 'src/icons/Send';

export interface CommissioningProps {
  token: string | null;
}

export function Commissioning({ token }: CommissioningProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const navigate = useNavigate();
  const [objectListIsLoading, setObjectListIsLoading] = useState(false);
  const [listedFiles, setListedFiles] = useState([]);
  const { addBannerMessage } = useContext(BannerMessageContext);
  const [showUploadPopup, setShowUploadPopup] = useState<boolean>(false);
  const fileInputRef = useRef<any>(null);
  const [file, setFile] = useState<File | null>(null);
  const [uploadingFile, setUploadingFile] = useState<boolean>(false);

  const handleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const getObjectList = useCallback(async () => {
    try {
      const url = `${WAS_BACK_END_API_URL}${URL_VERSION}/commissioning`;
      setFile(null);
      setObjectListIsLoading(true);
      const config: RequestInit = {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
      const response = await fetchAuthenticated<any>(url, config);

      if (response?.ok) {
        const files = response?.parsedBody?.result?.items || [];
        setListedFiles(files);
      }
      setObjectListIsLoading(false);
    } catch (e) {
      addBannerMessage({
        id: v4(),
        message: 'Uh oh! There was an error getting the patient data. Please refresh the page to try again.',
        type: 'error',
      });
    }
  }, [token, addBannerMessage]);

  useEffect(() => {
    getObjectList();
  }, [token, getObjectList]);

  const handleUpload = async (file: any) => {
    try {
      setUploadingFile(true);
      let fileId = v4();
      const url = `${WAS_BACK_END_API_URL}/w1/putcommissioningfile/${fileId}.jpeg`;
      const config: RequestInit = {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
      const response = await fetchAuthenticated<any>(url, config);

      if (response?.ok) {
        let preSignedUrl = response?.parsedBody?.result?.preSignedUrl;
        let bucketUrl = preSignedUrl.split('?');
        const result = await fetch(bucketUrl[0], {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': file.type || 'image/jpeg',
          },
        });

        if (result?.ok) {
          addBannerMessage({
            id: v4(),
            message: 'Successfully added to database!',
            type: 'success',
          });
          const url = `${WAS_BACK_END_API_URL}${URL_VERSION}/commissioning`;
          setFile(null);
          setObjectListIsLoading(true);
          const config: RequestInit = {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${token}`,
            },
          };
          const response = await fetchAuthenticated<any>(url, config);

          if (response?.ok) {
            const files = response?.parsedBody?.result?.items || [];
            setListedFiles(files);
          }
          setShowUploadPopup(false);
          setFile(null);
        } else {
          addBannerMessage({
            id: v4(),
            message: 'Error adding to database. Please try again!',
            type: 'error',
          });
        }
      }
      setUploadingFile(false);
    } catch (e) {
      setUploadingFile(false);
      addBannerMessage({
        id: v4(),
        message: JSON.stringify(e),
        type: 'error',
      });
    }
  };

  const handleFileChange = async (event: any) => {
    const file = event.target.files[0];
    setFile(file);
  };

  const handleUploadClick = async () => {
    if (file) {
      await handleUpload(file);
    } else {
      if (fileInputRef && fileInputRef?.current) fileInputRef.current.click();
    }
  };

  const cancelFileUpload = async () => {
    setShowUploadPopup(!showUploadPopup);
    setFile(null);
  };

  const content = (
    <>
      {!objectListIsLoading ? (
        <div className="treatmentRoom">
          {Object.entries(listedFiles).map(([key, listItem]: [string, any], index: number) => (
            <>
              <div className="roomheader">
                <div className="headerText">Treatment Room {Number(key[key.length - 1]) >= 0 ? key[key.length - 1] : 'Research'}</div>
              </div>
              <div className="roomtable">
                <List
                  token={token}
                  listedFiles={listItem}
                  key={listItem['Key']}
                  tableName={Number(key[key.length - 1]) >= 0 ? key[key.length - 1] : 'Research'}
                  showUploadPopup={(show) => setShowUploadPopup(show)}
                />
              </div>
            </>
          ))}
        </div>
      ) : (
        <div className="loadingSpinner">
          <LoadingSpinner width={largeSpinner} height={largeSpinner} />
        </div>
      )}
    </>
  );

  const uploadPopup = (
    <div className="upload-popup">
      <div className="popup-contents">
        <div className="title">Upload Dose Engine Data </div>
        <div className="info">
          {true ? <div>Upload dose engine data for the following:</div> : <div className="warninglabel">CAUTION! New upload will overwrite existing machine data.</div>}
        </div>
        <div className="treatmentroom">
          <div>Treatment Room:</div>
          <div>1</div>
        </div>
        <div className="collimeter">
          <div>Collimeter:</div>
          <div> 10cm x 10cm</div>
        </div>
        <input type="file" ref={fileInputRef} onChange={handleFileChange} style={{ display: 'none' }} />
        <div className="package-upload">
          <div>Package Upload:</div>
          <button className="filename" onClick={() => handleUploadClick()}>
            {file ? file?.name : 'Click to Upload....'}
          </button>
        </div>
        <div className="controls">
          <Button
            label={uploadingFile ? '' : 'Upload'}
            icon={uploadingFile ? <LoadingSpinner height={smallSpinner} width={smallSpinner} /> : <Send />}
            iconPlacement={'left'}
            className={'button-upload'}
            onClick={() => handleUploadClick()}
          ></Button>
          <Button label={'Cancel'} icon={<XClose />} iconPlacement={'left'} onClick={() => cancelFileUpload()} className={'go-back-button'} width="17%"></Button>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <Card title={'Commissioning'} body={isExpanded ? content : <></>} onExpand={handleExpanded} expandable={true} expanded={isExpanded} />
      {showUploadPopup && isExpanded && !objectListIsLoading ? uploadPopup : null}
      <div className="patient-controls">
        <Button
          label={'Back'}
          className={'go-back-button'}
          onClick={() => {
            navigate('/');
          }}
          icon={<AngleLeft />}
          iconPlacement={'left'}
        />
      </div>
    </div>
  );
}
